import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GateStepper } from "./GateStepper";
import { NdaSignPad } from "./NdaSignPad";
import { DEFAULT_VIEWER_LOGO, ViewerPortalHeader } from "./ViewerPortalHeader";

export type VisitorGatePreviewProps = {
  roomName: string;
  welcomeMessage?: string;
  bannerUrl?: string;
  logoUrl?: string;
  brandPrimary?: string;
  ndaRequired?: boolean;
  ndaTitle?: string;
  ndaBodyHtml?: string;
  previewEmail?: string;
  previewName?: string;
  showLabel?: boolean;
};

type PreviewStep = "email_verify" | "nda";

function maskPreviewEmail(email: string): string {
  const trimmed = email.trim();
  if (!trimmed.includes("@")) return trimmed;
  return trimmed.replace(/(.{1}).*(@.*)/, "$1***$2");
}

const STEP_LABELS: Record<PreviewStep, string> = {
  email_verify: "Email",
  nda: "NDA",
};

export function VisitorGatePreview({
  roomName,
  welcomeMessage,
  bannerUrl,
  logoUrl,
  brandPrimary = "var(--brand-primary)",
  ndaRequired = false,
  ndaTitle = "Non-Disclosure Agreement",
  ndaBodyHtml,
  previewEmail = "",
  previewName = "",
  showLabel = false,
}: VisitorGatePreviewProps) {
  const gates = useMemo(
    () => (ndaRequired ? (["email_verify", "nda"] as PreviewStep[]) : (["email_verify"] as PreviewStep[])),
    [ndaRequired],
  );
  const [previewStep, setPreviewStep] = useState<PreviewStep>("email_verify");

  const emailTrimmed = previewEmail.trim();
  const hasInviteEmail = emailTrimmed.includes("@");
  const maskedEmail = hasInviteEmail ? maskPreviewEmail(emailTrimmed) : null;

  const headerSubtitle = maskedEmail
    ? `Invited: ${maskedEmail}`
    : welcomeMessage?.trim() || "Enter your email to continue.";

  const ndaBodyDisplay =
    ndaBodyHtml?.trim() ||
    "<p class='muted'>NDA text will appear here once configured on the Access tab.</p>";

  const gateFooter = (
    <>
      <p className="muted viewer-gate__footer">Your email may appear as a watermark on documents.</p>
      <Link to="/privacy">Privacy notice</Link>
    </>
  );

  return (
    <div className="visitor-gate-preview">
      {showLabel ? <p className="visitor-gate-preview__label">Visitor preview</p> : null}
      {gates.length > 1 ? (
        <div className="visitor-gate-preview__step-pick row" role="tablist" aria-label="Preview step">
          {gates.map((step) => (
            <button
              key={step}
              type="button"
              role="tab"
              aria-selected={previewStep === step}
              className={previewStep === step ? "" : "ghost"}
              onClick={() => setPreviewStep(step)}
            >
              {STEP_LABELS[step]}
            </button>
          ))}
        </div>
      ) : null}
      <div className="visitor-gate-preview__frame" aria-label="Visitor gate preview">
        <div className="viewer-shell viewer-shell--preview">
          <ViewerPortalHeader
            variant="gate"
            roomName={roomName}
            subtitle={headerSubtitle}
            bannerUrl={bannerUrl}
            logoUrl={logoUrl?.trim() || DEFAULT_VIEWER_LOGO}
            brandPrimary={brandPrimary}
          />
          {previewStep === "email_verify" ? (
            <div className="stack viewer-gate" aria-hidden="true">
              <GateStepper gates={gates} current={previewStep} />
              <h2 className="page-title" style={{ marginTop: 0 }}>
                Enter your email
              </h2>
              <p className="muted" style={{ marginTop: 0 }}>
                {maskedEmail
                  ? `Access is restricted to ${maskedEmail}. Confirm it to continue.`
                  : "Enter your work email to continue."}
              </p>
              <label>
                Work email
                <input
                  className="input"
                  type="email"
                  readOnly
                  value={emailTrimmed}
                  placeholder="visitor@company.com"
                />
              </label>
              <label>
                Your name
                <input className="input" readOnly value={previewName} placeholder="Optional" />
              </label>
              <div className="turnstile-host turnstile-host--mock" aria-hidden="true">
                <span className="muted">Bot check</span>
              </div>
              <button type="button" disabled>
                Continue
              </button>
              {gateFooter}
            </div>
          ) : (
            <article className="stack viewer-gate" aria-hidden="true">
              <GateStepper gates={gates} current={previewStep} />
              <h2 className="page-title" style={{ marginTop: 0 }}>
                {ndaTitle}
              </h2>
              <div dangerouslySetInnerHTML={{ __html: ndaBodyDisplay }} />
              <div className="stack">
                <label>
                  Full legal name
                  <input className="input" readOnly value={previewName} placeholder="Full legal name" />
                </label>
                <NdaSignPad onChange={() => {}} />
                <button type="button" disabled>
                  Sign & continue
                </button>
                {gateFooter}
              </div>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
